/**
 * Tile calculation utilities for Web Mercator map tiles.
 *
 * Provides functions for:
 * - Converting lat/lng to tile coordinates
 * - Getting tile bounds
 * - Determining visible tiles based on FOV
 * - Mapping FOV to zoom level
 */

import { GLOBE_CONFIG } from '../constants'
import type { TileCoordinate, TileBounds, VisibleTile } from '../types'

/**
 * Convert latitude/longitude to Web Mercator tile coordinates.
 *
 * Based on standard slippy map tile numbering:
 * - X increases from west to east (0 at -180°)
 * - Y increases from north to south (0 at ~85.05°)
 *
 * @param latitude - Latitude in degrees
 * @param longitude - Longitude in degrees
 * @param zoom - Zoom level (0-22)
 * @returns Tile coordinate at the given zoom level
 */
export function latLngToTile(
  latitude: number,
  longitude: number,
  zoom: number
): TileCoordinate {
  // Clamp latitude to Web Mercator bounds
  const lat = Math.max(
    GLOBE_CONFIG.MIN_LATITUDE,
    Math.min(GLOBE_CONFIG.MAX_LATITUDE, latitude)
  )

  // Number of tiles at this zoom level (2^zoom)
  const n = Math.pow(2, zoom)

  // Convert longitude to tile X
  const x = Math.floor(((longitude + 180) / 360) * n)

  // Convert latitude to tile Y using Web Mercator projection
  const latRad = (lat * Math.PI) / 180
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  )

  return {
    x: Math.max(0, Math.min(n - 1, x)),
    y: Math.max(0, Math.min(n - 1, y)),
    z: zoom,
  }
}

/**
 * Get the geographic bounds of a tile.
 *
 * @param tile - Tile coordinate
 * @returns Geographic bounds (north, south, east, west)
 */
export function tileToLatLngBounds(tile: TileCoordinate): TileBounds {
  const n = Math.pow(2, tile.z)

  // Longitude bounds
  const west = (tile.x / n) * 360 - 180
  const east = ((tile.x + 1) / n) * 360 - 180

  // Latitude bounds (Web Mercator inverse)
  const north = (Math.atan(Math.sinh(Math.PI * (1 - (2 * tile.y) / n))) * 180) / Math.PI
  const south =
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * (tile.y + 1)) / n))) * 180) / Math.PI

  return { north, south, east, west }
}

/**
 * Get the center point of a tile in geographic coordinates.
 *
 * @param tile - Tile coordinate
 * @returns Center latitude and longitude
 */
export function tileCenterLatLng(tile: TileCoordinate): {
  latitude: number
  longitude: number
} {
  const bounds = tileToLatLngBounds(tile)
  return {
    latitude: (bounds.north + bounds.south) / 2,
    longitude: (bounds.east + bounds.west) / 2,
  }
}

/**
 * Map field of view to an appropriate zoom level.
 *
 * Inverse of zoomToFov - calculates zoom from FOV.
 * Smaller FOV = more detail needed = higher zoom level
 *
 * @param fov - Field of view in degrees
 * @returns Zoom level (clamped to MIN_ZOOM - MAX_ZOOM)
 */
export function fovToZoom(fov: number): number {
  // Inverse of zoomToFov formula:
  // fov = (360 / 2^zoom) * 10 * 1.05
  // 2^zoom = 360 * 10 * 1.05 / fov = 3780 / fov
  // zoom = log2(3780 / fov)
  const maxTilesPerSide = 10
  const padding = 1.05
  const zoom = Math.log2((360 * maxTilesPerSide * padding) / fov)

  return Math.round(
    Math.max(GLOBE_CONFIG.MIN_ZOOM, Math.min(GLOBE_CONFIG.MAX_ZOOM, zoom))
  )
}

/**
 * Map zoom level to field of view.
 *
 * FOV is calculated to match the visible tile coverage area.
 * This ensures tiles fill most of the view at any zoom level.
 *
 * @param zoom - Zoom level (0-22)
 * @returns Field of view in degrees
 */
export function zoomToFov(zoom: number): number {
  // FOV should match the tile coverage area
  // We show up to 10 tiles per side, each covering 360/2^zoom degrees
  const maxTilesPerSide = 10
  const degreesPerTile = 360 / Math.pow(2, zoom)
  const tileSpan = degreesPerTile * maxTilesPerSide

  // Minimal padding (5%) so tiles nearly fill the view
  const fov = tileSpan * 1.05

  // Clamp to valid FOV range
  return Math.max(GLOBE_CONFIG.MIN_FOV, Math.min(GLOBE_CONFIG.MAX_FOV, fov))
}

/**
 * Calculate how many degrees of latitude/longitude are visible at a given FOV.
 *
 * For a concave hemisphere viewed from inside, we can see more area at the
 * edges than a flat projection would suggest. The hemisphere curves around
 * the viewer, so the effective visible span is larger.
 *
 * @param fov - Field of view in degrees
 * @returns Approximate visible span in degrees (with generous padding)
 */
export function fovToVisibleSpan(fov: number): number {
  // The hemisphere extends 90° from center to edge in all directions.
  // With a wide FOV, we can see nearly the entire hemisphere.
  // Using 1.5x multiplier provides adequate coverage without loading
  // excessive tiles, improving performance.
  return Math.min(180, fov * 1.5)
}

/**
 * Get all tiles visible from a given view position.
 *
 * Calculates tiles needed to cover the visible area based on
 * the center position and field of view. Handles dateline (±180°)
 * wrapping by returning tiles with a renderOffset for seamless display.
 *
 * @param centerLat - Center latitude of view
 * @param centerLng - Center longitude of view
 * @param fov - Field of view in degrees
 * @param zoom - Zoom level (optional, calculated from FOV if not provided)
 * @param maxTileZoom - Maximum tile zoom level (default 8)
 * @returns Array of visible tile coordinates with optional render offsets
 */
export function getVisibleTiles(
  centerLat: number,
  centerLng: number,
  fov: number,
  zoom?: number,
  maxTileZoom: number = 8
): VisibleTile[] {
  // Ensure zoom is an integer - tile coordinates require whole numbers
  // Cap tile zoom to maxTileZoom for readability
  const rawZoom = Math.round(zoom ?? fovToZoom(fov))
  const effectiveZoom = Math.min(rawZoom, maxTileZoom)

  // Calculate visible span based on FOV
  // For a hemisphere, the FOV determines how much of the curved surface we see
  const degreesPerTile = 360 / Math.pow(2, effectiveZoom)

  // Limit tiles per side to keep total count manageable (~50-100 tiles)
  const maxTilesPerSide = 6
  const maxSpanForZoom = degreesPerTile * maxTilesPerSide

  // Use FOV to determine view area, capped by tile availability
  // Multiply by 1.5 to ensure tiles fill the curved hemisphere view
  const baseSpan = Math.min(fov * 1.5, maxSpanForZoom)

  // Account for widescreen aspect ratio
  const aspectRatio = 1.8
  const latSpan = baseSpan
  const lngSpan = baseSpan * aspectRatio

  // Calculate bounds of visible area
  const north = Math.min(GLOBE_CONFIG.MAX_LATITUDE, centerLat + latSpan / 2)
  const south = Math.max(GLOBE_CONFIG.MIN_LATITUDE, centerLat - latSpan / 2)
  let east = centerLng + lngSpan / 2
  let west = centerLng - lngSpan / 2

  // Check if view spans the dateline (±180°)
  const spansDatlineEast = east > 180
  const spansDatlineWest = west < -180

  const tiles: VisibleTile[] = []
  const n = Math.pow(2, effectiveZoom)

  if (spansDatlineEast) {
    // View extends past +180° into negative longitudes
    // Example: center=170°, halfSpan=20° → west=150°, east=190°

    // Add tiles from west to +180° (canonical range, no offset)
    addTilesForLngRange(tiles, north, south, west, 180, effectiveZoom, n, 0)

    // Add tiles from -180° to wrapped east (with +360° offset for rendering)
    // These tiles are at negative longitudes but should render at >180°
    const wrappedEast = east - 360 // e.g., 190° - 360° = -170°
    addTilesForLngRange(tiles, north, south, -180, wrappedEast, effectiveZoom, n, 360)
  } else if (spansDatlineWest) {
    // View extends past -180° into positive longitudes
    // Example: center=-170°, halfSpan=20° → west=-190°, east=-150°

    // Add tiles from wrapped west to +180° (with -360° offset for rendering)
    // These tiles are at positive longitudes but should render at <-180°
    const wrappedWest = west + 360 // e.g., -190° + 360° = 170°
    addTilesForLngRange(tiles, north, south, wrappedWest, 180, effectiveZoom, n, -360)

    // Add tiles from -180° to east (canonical range, no offset)
    addTilesForLngRange(tiles, north, south, -180, east, effectiveZoom, n, 0)
  } else {
    // Normal case - no dateline wrapping needed
    addTilesForLngRange(tiles, north, south, west, east, effectiveZoom, n, 0)
  }

  return tiles
}

/**
 * Helper to add tiles for a longitude range to the tiles array.
 */
function addTilesForLngRange(
  tiles: VisibleTile[],
  north: number,
  south: number,
  west: number,
  east: number,
  zoom: number,
  n: number,
  renderOffset: number
): void {
  const nwTile = latLngToTile(north, west, zoom)
  const seTile = latLngToTile(south, east, zoom)

  for (let y = nwTile.y; y <= seTile.y; y++) {
    for (let x = nwTile.x; x <= seTile.x; x++) {
      // Handle wrapping for x (longitude can wrap around)
      const wrappedX = ((x % n) + n) % n
      tiles.push({
        x: wrappedX,
        y,
        z: zoom,
        renderOffset: renderOffset || undefined,
      })
    }
  }
}

/**
 * Generate a unique string key for a tile coordinate.
 *
 * Used for caching and deduplication. Includes renderOffset
 * to distinguish wraparound tiles from their canonical versions.
 *
 * @param tile - Tile coordinate (optionally with renderOffset)
 * @returns Unique string key
 */
export function getTileKey(tile: TileCoordinate | VisibleTile): string {
  const base = `${tile.z}/${tile.x}/${tile.y}`
  const offset = 'renderOffset' in tile && tile.renderOffset ? tile.renderOffset : 0
  return offset ? `${base}@${offset}` : base
}

/**
 * Parse a tile key back into coordinates.
 *
 * @param key - Tile key string (z/x/y format)
 * @returns Tile coordinate or null if invalid
 */
export function parseTileKey(key: string): TileCoordinate | null {
  const parts = key.split('/')
  if (parts.length !== 3) return null

  const z = parseInt(parts[0], 10)
  const x = parseInt(parts[1], 10)
  const y = parseInt(parts[2], 10)

  if (isNaN(z) || isNaN(x) || isNaN(y)) return null

  return { x, y, z }
}

/**
 * Build a tile URL from template and coordinates.
 *
 * Replaces {x}, {y}, {z}, and {s} placeholders.
 *
 * @param template - URL template with placeholders
 * @param tile - Tile coordinate
 * @param serverCount - Number of servers for load balancing (default 4)
 * @returns Complete tile URL
 */
export function buildTileUrl(
  template: string,
  tile: TileCoordinate,
  serverCount: number = 4
): string {
  // Deterministic server selection based on tile coordinates
  const server = (tile.x + tile.y) % serverCount

  return template
    .replace('{x}', String(tile.x))
    .replace('{y}', String(tile.y))
    .replace('{z}', String(tile.z))
    .replace('{s}', String(server))
}

/**
 * Check if two tile coordinates are equal.
 *
 * @param a - First tile
 * @param b - Second tile
 * @returns True if tiles are identical
 */
export function tilesEqual(a: TileCoordinate, b: TileCoordinate): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z
}

/**
 * Get the parent tile coordinate (one zoom level up).
 *
 * Each tile at zoom Z has a parent at zoom Z-1 that covers 4x the area.
 *
 * @param tile - Child tile coordinate
 * @returns Parent tile coordinate, or null if at zoom 0
 */
export function getParentTile(tile: TileCoordinate): TileCoordinate | null {
  if (tile.z <= 0) return null

  return {
    x: Math.floor(tile.x / 2),
    y: Math.floor(tile.y / 2),
    z: tile.z - 1,
  }
}

/**
 * Get the UV offset and scale for rendering a child tile using its parent's texture.
 *
 * When a tile at zoom Z is not loaded, we can use the parent tile (zoom Z-1) and
 * show only the relevant quadrant. This function returns the UV transformation needed.
 *
 * Note: Web Mercator tiles have y=0 at NORTH, increasing southward.
 * Our UV space has v=0 at SOUTH (due to the 1-v flip in geometry creation).
 * So we need to flip the Y offset to match.
 *
 * @param child - The child tile we want to render
 * @param ancestor - The ancestor tile we're using as fallback
 * @returns UV offset [u, v] and scale, or null if not an ancestor
 */
export function getAncestorUVTransform(
  child: TileCoordinate,
  ancestor: TileCoordinate
): { uvOffset: [number, number]; uvScale: number } | null {
  const zoomDiff = child.z - ancestor.z
  if (zoomDiff <= 0) return null

  // Scale factor: each zoom level doubles the resolution
  const scale = 1 / Math.pow(2, zoomDiff)

  // Calculate the child's position relative to the ancestor
  // This gives us which "sub-tile" of the ancestor this child represents
  const divisor = Math.pow(2, zoomDiff)
  const relativeX = child.x - ancestor.x * divisor
  const relativeY = child.y - ancestor.y * divisor

  // Convert to UV offset (0 to 1 range)
  const offsetX = relativeX / divisor
  // Flip Y because tile Y increases southward but UV v increases northward
  const offsetY = 1 - (relativeY + 1) / divisor

  // Validate the child is actually within this ancestor
  if (relativeX < 0 || relativeX >= divisor || relativeY < 0 || relativeY >= divisor) {
    return null
  }

  return {
    uvOffset: [offsetX, offsetY],
    uvScale: scale,
  }
}

/**
 * Get neighboring tiles (including diagonals).
 *
 * Useful for prefetching tiles that may become visible.
 *
 * @param tile - Center tile
 * @returns Array of 8 neighboring tiles (clamped to valid range)
 */
export function getNeighborTiles(tile: TileCoordinate): TileCoordinate[] {
  const n = Math.pow(2, tile.z)
  const neighbors: TileCoordinate[] = []

  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue // Skip center

      const x = ((tile.x + dx) % n + n) % n // Wrap longitude
      const y = tile.y + dy

      // Only add if y is valid (no wrapping for latitude)
      if (y >= 0 && y < n) {
        neighbors.push({ x, y, z: tile.z })
      }
    }
  }

  return neighbors
}

/**
 * Normalize a longitude value to the range [-180, 180).
 *
 * Handles both positive and negative values correctly by using the
 * formula: ((lng % 360) + 540) % 360 - 180
 *
 * This works because:
 * - Adding 540 (360 + 180) handles negative modulo and shifts to [0, 360)
 * - Subtracting 180 brings it back to [-180, 180)
 *
 * @param lng - Longitude in degrees (any range)
 * @returns Normalized longitude in [-180, 180)
 *
 * @example
 * normalizeLongitude(200)   // Returns -160
 * normalizeLongitude(-200)  // Returns 160
 * normalizeLongitude(370)   // Returns 10
 * normalizeLongitude(-180)  // Returns -180
 */
export function normalizeLongitude(lng: number): number {
  return ((lng % 360) + 540) % 360 - 180
}

/**
 * Apply a render offset to tile bounds for dateline wrapping.
 *
 * When tiles span the dateline (±180°), some tiles need to be rendered
 * at an offset position. This function adjusts the longitude bounds
 * by the specified offset.
 *
 * @param bounds - Original tile bounds
 * @param offset - Longitude offset (typically 0, 360, or -360)
 * @returns New bounds with adjusted longitudes, or original if offset is 0/undefined
 *
 * @example
 * const bounds = { north: 40, south: 30, east: -170, west: -180 }
 * const adjusted = applyRenderOffset(bounds, 360)
 * // Returns { north: 40, south: 30, east: 190, west: 180 }
 */
export function applyRenderOffset(
  bounds: TileBounds,
  offset: number | undefined
): TileBounds {
  if (!offset) return bounds
  return {
    ...bounds,
    west: bounds.west + offset,
    east: bounds.east + offset,
  }
}
